import { COUNTRIES } from "./site";

export const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Region hints used to add variation to programmatic content.
const REGIONS = {
  Africa: ["Nigeria", "Kenya", "Ghana", "Egypt", "Tanzania", "Uganda", "Ethiopia", "Angola", "Cameroon", "Ivory Coast", "DR Congo", "Chad", "Mali", "Senegal", "Zambia", "Zimbabwe", "Mozambique", "Gabon", "Benin", "Burkina Faso", "Sierra Leone", "Liberia", "Rwanda", "Sudan", "Algeria", "Morocco", "Tunisia", "Libya", "Togo", "Niger", "Guinea", "Gambia", "Madagascar", "Malawi", "Namibia", "Djibouti", "Eritrea", "Equatorial Guinea", "Central African Republic", "Somalia"],
  Asia: ["India", "China", "Vietnam", "Indonesia", "Cambodia", "Myanmar", "Pakistan", "Bangladesh", "Sri Lanka", "Nepal", "Laos", "Bhutan", "Mongolia", "Kazakhstan", "Uzbekistan", "Turkmenistan", "Tajikistan", "Kyrgyzstan"],
  "Middle East": ["Saudi Arabia", "United Arab Emirates", "Bahrain", "Qatar", "Kuwait", "Oman", "Jordan", "Lebanon", "Yemen", "Iraq", "Syria", "Azerbaijan"],
  Europe: ["Russia", "Turkey"],
  "Latin America": ["Brazil", "Argentina", "Bolivia", "Suriname"],
  Oceania: ["Australia", "Papua New Guinea"],
};

const regionOf = (name) => {
  for (const [region, list] of Object.entries(REGIONS)) {
    if (list.includes(name)) return region;
  }
  return "worldwide";
};

export const COUNTRY_LIST = COUNTRIES.map((name) => ({
  name,
  slug: slugify(name),
  region: regionOf(name),
}));

export const findCountry = (slug) => COUNTRY_LIST.find((c) => c.slug === slug);

// Official embassy / consulate websites (U.S.-based). Only high-confidence links.
export const EMBASSY_LINKS = {
  china: "http://us.china-embassy.gov.cn/eng/",
  india: "https://www.indianembassyusa.gov.in/",
  brazil: "https://washington.itamaraty.gov.br/en-us/",
  russia: "https://washington.mid.ru/en/",
  vietnam: "https://vietnamembassy-usa.org/",
  nigeria: "https://www.nigeriaembassyusa.org/",
  "saudi-arabia": "https://www.saudiembassy.net/",
  "united-arab-emirates": "https://www.uae-embassy.org/",
  egypt: "https://www.egyptembassy.net/",
  kenya: "https://kenyaembassydc.org/",
  ghana: "https://www.ghanaembassy.org/",
  turkey: "http://washington.emb.mfa.gov.tr/",
  indonesia: "https://www.embassyofindonesia.org/",
  tanzania: "https://us.tzembassy.go.tz/",
  ethiopia: "https://ethiopianembassy.org/",
  cameroon: "https://www.cameroonembassyusa.org/",
  qatar: "https://washington.embassy.qa/en/",
  kuwait: "https://www.kuwaitembassy.us/",
  jordan: "http://www.jordanembassyus.org/",
  pakistan: "https://www.embassyofpakistanusa.org/",
  bangladesh: "https://www.bdembassyusa.org/",
  "sri-lanka": "https://slembassyusa.org/",
  nepal: "https://us.nepalembassy.gov.np/",
  australia: "https://usa.embassy.gov.au/",
  morocco: "http://www.embassyofmorocco.us/",
  argentina: "https://eeeuu.cancilleria.gob.ar/en",
  kazakhstan: "https://www.gov.kz/memleket/entities/mfa-washington?lang=en",
  bahrain: "https://www.mofa.gov.bh/washington/",
  zimbabwe: "https://www.zimembassydc.org/",
  zambia: "https://www.zambiaembassy.org/",
  mongolia: "https://mongolianembassy.us/",
  "dr-congo": "https://www.ambardcusa.org/",
  senegal: "https://www.senegalembassy-us.org/",
  uganda: "https://washington.mofa.go.ug/",
  rwanda: "https://us.embassy.gov.rw/",
  oman: "https://www.cultureofoman.com/",
  lebanon: "https://www.lebanonembassyus.org/",
  cambodia: "https://www.embassyofcambodiadc.org/",
  laos: "https://laoembassy.com/",
  myanmar: "https://mewashingtondc.org/",
  uzbekistan: "https://uzbekistan.org/",
  tunisia: "https://www.tunisianembassy.org/",
  sudan: "https://sudanembassy.org/",
  libya: "https://www.embassyoflibyadc.org/",
  azerbaijan: "https://washington.mfa.gov.az/",
  kyrgyzstan: "https://mfa.gov.kg/en/dm/-Embassy-of-the-Kyrgyz-Republic-in-the-USA-and-Canada",
  tajikistan: "https://mfa.tj/en/washington",
  bolivia: "https://www.boliviawdc.org/",
  yemen: "https://www.yemenembassy.org/",
  "ivory-coast": "https://usa.diplomatie.gouv.ci/",
  gabon: "https://www.gabonembassyusa.org/en",
  benin: "https://beninembassy.us/",
  mali: "https://www.maliembassy.us/",
  niger: "https://embassyofniger-usa.org/",
};

export const embassyLink = (slug) => EMBASSY_LINKS[slug] || null;

// Tailored requirement sets for high-traffic e-visa countries.
// Countries not listed here fall back to the generic tourist/business lists.
export const COUNTRY_REQUIREMENTS = {
  india: {
    note: "India uses an electronic visa (e-Visa) — your passport is not physically submitted. We complete and file the online application for you.",
    tourist: [
      { title: "Completed India e-Visa Application", desc: "We prepare and submit your online e-Visa application for you." },
      { title: "Passport Photo", desc: "Recent 2×2 inch color photo on a white background, no glasses." },
      { title: "Current Valid U.S. Passport", desc: "Valid at least 6 months from arrival with at least 2 blank pages." },
      { title: "Passport Bio-Page Scan", desc: "A clear color scan of your passport photo/data page (JPEG or PDF)." },
      { title: "Travel Itinerary", desc: "Your intended arrival and departure dates and cities you'll visit." },
    ],
    business: [
      { title: "Completed India e-Visa Application", desc: "We prepare and submit your online Business e-Visa application for you." },
      { title: "Passport Photo", desc: "Recent 2×2 inch color photo on a white background, no glasses." },
      { title: "Current Valid U.S. Passport", desc: "Valid at least 6 months from arrival with at least 2 blank pages." },
      { title: "Passport Bio-Page Scan", desc: "A clear color scan of your passport photo/data page (JPEG or PDF)." },
      { title: "Letter of Invitation", desc: "From the Indian company you are visiting, in English." },
      { title: "Copy of Your Business Card", desc: "Or a company introduction letter on official letterhead." },
    ],
  },
  brazil: {
    note: "Brazil reintroduced an electronic visitor visa (e-Visa) for U.S. citizens in 2025. We handle the online filing on your behalf.",
    tourist: [
      { title: "Completed Brazil e-Visa Application", desc: "We prepare and submit your online e-Visa application for you." },
      { title: "Passport Photo", desc: "2×2 inch color photo on a white background, taken within the last 6 months, no glasses." },
      { title: "Current Valid U.S. Passport", desc: "Valid at least 6 months beyond entry with at least 1 blank page." },
      { title: "Passport Bio-Page Scan", desc: "A color scan of your passport data page valid through your return date." },
      { title: "Round-Trip Itinerary", desc: "Flight reservation showing your entry and exit dates." },
      { title: "Proof of Accommodation", desc: "Hotel booking or the address of your host in Brazil." },
      { title: "Proof of Funds", desc: "A recent bank statement, if requested." },
    ],
    business: [
      { title: "Completed Brazil e-Visa Application", desc: "We prepare and submit your online Business e-Visa application for you." },
      { title: "Passport Photo", desc: "2×2 inch color photo on a white background, taken within the last 6 months, no glasses." },
      { title: "Current Valid U.S. Passport", desc: "Valid at least 6 months beyond entry with at least 1 blank page." },
      { title: "Passport Bio-Page Scan", desc: "A color scan of your passport data page valid through your return date." },
      { title: "Notarized Letter of Invitation", desc: "From the Brazilian company you are visiting." },
      { title: "Company Business Letter", desc: "On official letterhead stating the purpose and duration of travel." },
    ],
  },
  vietnam: {
    note: "Vietnam issues an electronic visa (e-Visa) valid for up to 90 days. We complete and file the online application for you.",
    tourist: [
      { title: "Completed Vietnam e-Visa Application", desc: "We prepare and submit your online e-Visa application for you." },
      { title: "Passport Photo", desc: "4×6 cm color photo on a white background, no glasses, taken within the last 6 months." },
      { title: "Current Valid U.S. Passport", desc: "Valid at least 6 months beyond entry with at least 2 blank pages." },
      { title: "Passport Bio-Page Scan", desc: "A high-resolution scan of the full passport data page, legible text." },
      { title: "Travel Details", desc: "Entry/exit dates, your port of entry, and your accommodation address in Vietnam." },
    ],
    business: [
      { title: "Completed Vietnam e-Visa Application", desc: "We prepare and submit your online Business e-Visa application for you." },
      { title: "Passport Photo", desc: "4×6 cm color photo on a white background, no glasses, taken within the last 6 months." },
      { title: "Current Valid U.S. Passport", desc: "Valid at least 6 months beyond entry with at least 2 blank pages." },
      { title: "Passport Bio-Page Scan", desc: "A high-resolution scan of the full passport data page, legible text." },
      { title: "Sponsorship / Approval Letter", desc: "From the Vietnamese company or a licensed local sponsor." },
      { title: "Company Business Letter", desc: "On official letterhead stating the purpose and duration of travel." },
    ],
  },
};

export const countryRequirements = (slug) => COUNTRY_REQUIREMENTS[slug] || null;
